import { commandCenterTools } from '../config/commandCenterTools';

/**
 * prioritizeTools — Given the current user context, return an ordered
 * list of tool IDs (highest-priority first).  Preserves ALL routes —
 * this only reorders, never removes.
 *
 * Context inputs (all optional; safe defaults):
 *   issueCategory     'safety' | 'discipline' | 'grievance' | 'contract' | ...
 *   confidence        0.0–1.0 confidence of the most recent answer
 *   riskLevel         'low' | 'medium' | 'high'
 *   hasOpenCase       boolean — user has an active Case Locker case
 *   hasDraftGrievance boolean — user has an unfinished grievance draft
 *   hasFactFinderDraft boolean — user has an unfinished fact finder
 */
export function prioritizeTools(ctx = {}) {
  const scored = commandCenterTools.map((t) => ({ ...t, score: t.priority || 0 }));

  const bump = (id, by) => {
    const t = scored.find((x) => x.id === id);
    if (t) t.score += by;
  };

  // ── Explicit context signals ─────────────────────────────────────
  if (ctx.hasDraftGrievance) bump('grievance-builder', 40);
  if (ctx.hasFactFinderDraft) bump('fact-finder', 40);
  if (ctx.hasOpenCase) bump('case-locker', 45);

  // ── Confidence & risk ────────────────────────────────────────────
  if (typeof ctx.confidence === 'number' && ctx.confidence < 0.5) {
    bump('discipline-predictor', 20);
    bump('report-issue', 25);
  }
  if (ctx.riskLevel === 'high') {
    bump('discipline-predictor', 30);
    bump('report-issue', 25);
  }

  // ── Issue-category weighting ─────────────────────────────────────
  const cat = String(ctx.issueCategory || '').toLowerCase();
  if (cat.includes('safety'))       bump('safety-rules', 40);
  if (cat.includes('discipline'))   bump('discipline-predictor', 40);
  if (cat.includes('grievance'))    bump('grievance-builder', 30);
  if (cat.includes('investigation') || cat.includes('fact')) bump('fact-finder', 30);
  if (cat.includes('contract'))     bump('library', 20);
  if (cat.includes('harassment') || cat.includes('retaliat')) {
    bump('report-issue', 30);
    bump('fact-finder', 15);
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.map((t) => t.id);
}

/**
 * pickRecommendedTools — Return the top N ordered tool objects.
 * Guarantees a spread: at least one "Get Answers" tool always appears
 * in the top slot for users with no context, so the page never feels
 * empty on first visit.
 */
export function pickRecommendedTools(ctx = {}, n = 4) {
  const ids = prioritizeTools(ctx);
  const byId = new Map(commandCenterTools.map((t) => [t.id, t]));
  return ids.slice(0, n).map((id) => byId.get(id)).filter(Boolean);
}
